
import type { PropType } from 'vue'
import type { DataTableColumn, DataTableOption, Sort } from '../../models'

import { defineComponent } from 'vue'

import { useDateFormat } from '../../composables/use-date-format'

import { useScheduleFormat } from '../../composables/use-schedule-format'

import { AlignEnum, SortDirectionEnum } from '../../enums'
import { capitalizeSentence } from '../../filters'
import { UiCheckbox, UiMenu, UiPopover, UiRippleInk, UiTooltip } from '../keen-ui'
import CopyToClipboard from '../shared/copy-to-clipboard.component.vue'
import { EpbButton } from '../shared/epb-button'
import TransitionSize from '../transition-size.component.vue'

export default defineComponent({
  name: 'DataTableRow',
  components: {
    CopyToClipboard,
    EpbButton,
    TransitionSize,
    UiCheckbox,
    UiMenu,
    UiPopover,
    UiRippleInk,
    UiTooltip,
  },
  props: {
    columns: Array as PropType<Array<DataTableColumn>>,
    dataItem: Object as PropType<DataTableOption | unknown>,
    isTableHeader: Boolean,
    sort: Object as PropType<Sort>,
    rowIndex: Number,
    select: {
      type: Object as PropType<{ enabled: boolean; multiple: boolean }>,
      required: true,
    },
    allDataItemsAreSelected: Boolean,
    selection: {
      type: Array as PropType<Array<unknown>>,
      default: () => [],
    },
    selectionCheckboxesHidden: Boolean,
    hasChildren: Boolean,
    expandedChildrenIds: Array as PropType<Array<string>>,
    multiChildrenExpandEnabled: Boolean,
    highlightedRows: {
      type: Array as PropType<Array<unknown>>,
      default: () => [],
    },
    highlightedTitleDescription: String,
    rowsIdField: {
      type: String,
      default: 'id',
    },
    readOnly: Boolean,
  },
  data() {
    return {
      scheduleColumnId: ['criteria.schedule', 'schedule'],
      optionsColumnId: 'options',
      updateDateColumnIds: ['meta.updateDate', 'metadata.updated'],
      userColumnIds: ['meta.user', 'metadata.author'],
      enabledColumnIds: ['enabled', 'criteria.enabled'],
      optionIcons: {
        edit: 'edit',
        copy: 'clone',
        delete: 'trash',
        enable: 'enabled',
        disable: 'disabled',
      },
      capitalizeSentence,
    }
  },
  computed: {
    rowCellHtmlTag(): string {
      return this.isTableHeader ? 'th' : 'td'
    },
    checkboxValue(): boolean {
      if (this.isTableHeader) {
        return this.allDataItemsAreSelected
      }
      return this.isDataItemSelected(this.dataItem)
    },
    areChildrenExpanded(): boolean {
      return this.expandedChildrenIds.includes(this.dataItem[this.rowsIdField])
    },
    rowProps() {
      return {
        columns: this.columns,
        sort: this.sort,
        select: this.select,
        allDataItemsAreSelected: this.allDataItemsAreSelected,
        selection: this.selection,
        hasChildren: this.hasChildren,
        expandedChildrenIds: this.expandedChildrenIds,
        multiChildrenExpandEnabled: this.multiChildrenExpandEnabled,
        selectionCheckboxesHidden: this.selectionCheckboxesHidden,
        rowsIdField: this.rowsIdField,
      }
    },
  },
  methods: {
    getRowClass(dataItem: any): (string | Record<string, boolean>)[] {
      const rowClass: (string | Record<string, boolean>)[] = [
        {
          'epb-data-table-row--selectable': !this.isTableHeader && this.select.enabled,
          'epb-data-table-row--selected':
            !this.isTableHeader && this.select.enabled && this.isDataItemSelected(dataItem),
          'epb-data-table-row--head': this.isTableHeader,
        },
      ]
      if (dataItem) {
        rowClass.push(`epb-data-table-row--level-${dataItem.level}`)
        rowClass.push({
          'epb-data-table-row--highlighted': this.isDataItemInArray(dataItem, this.highlightedRows),
        })
      }
      return rowClass
    },
    onCheckboxChange(selectAll: boolean): void {
      if (!this.isTableHeader) {
        this.onRowClick(this.dataItem, 'checkbox')
      } else {
        this.$emit('selectAll', selectAll)
      }
    },
    getCellClass(column: DataTableColumn, index: number) {
      const columnSorted = !!this.sort && this.sort.field === this.getSortField(column)
      return {
        'epb-data-table-row__cell--root':
          index === 0 && !!this.dataItem && this.dataItem.level === 0,
        'epb-data-table-row__cell--sortable': column.sortable,
        'epb-data-table-row__cell--sorted-asc':
          this.isTableHeader && columnSorted && this.sort.direction === SortDirectionEnum.ASCENDING,
        'epb-data-table-row__cell--sorted-desc':
          this.isTableHeader &&
          columnSorted &&
          this.sort.direction === SortDirectionEnum.DESCENDING,
        'epb-data-table-row__cell--align-center':
          column.align === AlignEnum.CENTER || this.enabledColumnIds.includes(column.id),
        'epb-data-table-row__cell--align-end': column.align === AlignEnum.END,
        'epb-data-table-row__cell--unwrap-text': column.unwrapText,
      }
    },
    getSortField(column: DataTableColumn): string {
      return column.sortableId || column.id
    },
    getCellStyle(column: DataTableColumn) {
      const width =
        column.width ||
        {
          ...this.updateDateColumnIds.reduce(
            (columnIds, columnId) => ((columnIds[columnId] = '9rem'), columnIds),
            {},
          ),
          ...this.userColumnIds.reduce(
            (columnIds, columnId) => ((columnIds[columnId] = '8rem'), columnIds),
            {},
          ),
          ...this.enabledColumnIds.reduce(
            (columnIds, columnId) => ((columnIds[columnId] = '8rem'), columnIds),
            {},
          ),
        }[column.id] ||
        '100%'
      const cellStyles: any = {
        flexBasis: width,
        flexShrink: !width.includes('%') ? 0 : undefined,
      }
      return cellStyles
    },
    onColumnOptionClick(column: DataTableColumn, option: DataTableColumn['options'][0]): void {
      this.$refs.columnDropdown[0].close()
      this.$emit('columnOption', [column, option.id])
    },
    onOptionClick(option: DataTableOption): void {
      this.$refs.dropdown[0].close()
      this.$emit('option', option)
    },
    getOptionIcon(optionId: string): string {
      return this.optionIcons[optionId]
    },
    getCellContentPadding(columnIndex: number): string {
      if (!!this.dataItem && this.dataItem.level !== 0 && columnIndex === 0) {
        return `${25 * this.dataItem.level + 5}px`
      }
    },
    onHeaderClick(column: DataTableColumn): void {
      const sortField = this.getSortField(column)
      if (column.sortable) {
        const sort: Sort | undefined = {
          field: sortField,
          direction: SortDirectionEnum.ASCENDING,
        }
        if (!!this.sort && this.sort.field === sortField) {
          if (this.sort.direction === SortDirectionEnum.ASCENDING) {
            sort.direction = SortDirectionEnum.DESCENDING
          } else {
            sort.direction = SortDirectionEnum.ASCENDING
          }
        }
        this.$emit('sortChange', sort)
      }
    },
    onRowClick(dataItem: any, type?: string): void {
      if (this.select.enabled) {
        const indexOfDataItem = this.selection.findIndex(
          (selectedItem: any) => selectedItem[this.rowsIdField] === dataItem[this.rowsIdField],
        )
        let selection = this.selection.slice()
        if (indexOfDataItem >= 0) {
          selection.splice(indexOfDataItem, 1)
        } else {
          if (this.select.multiple) {
            selection.push(dataItem)
          } else {
            selection = [dataItem]
          }
        }
        this.$emit('selectionChange', selection)
      }
      if (type !== 'checkbox') {
        this.$emit('clickRow')
      } else {
        this.$emit('clickCheckbox')
      }
    },
    onCellClick(column: DataTableColumn): void {
      if (this.isTableHeader) {
        this.onHeaderClick(column)
      } else if (column.id !== 'options') {
        this.onRowClick(this.dataItem)
      }
    },
    onRowExpand(dataItem): void {
      const expandedChildrenIds =
        !this.multiChildrenExpandEnabled && dataItem.level === 0
          ? []
          : [...this.expandedChildrenIds]
      if (this.expandedChildrenIds.includes(dataItem[this.rowsIdField])) {
        ;[dataItem[this.rowsIdField], ...this.getChildrenIds(dataItem)].forEach((id: string) => {
          if (expandedChildrenIds.includes(id)) {
            expandedChildrenIds.splice(expandedChildrenIds.indexOf(id), 1)
          }
        })
      } else {
        expandedChildrenIds.push(dataItem[this.rowsIdField])
      }
      this.$emit('expandedChildrenChange', expandedChildrenIds)
    },
    getScheduleCellHtml(dataItem: any) {
      const schedule = dataItem.schedule || dataItem.criteria?.schedule
      return useScheduleFormat(schedule)
    },
    getCellValue(dataItem: any, { id }: DataTableColumn): any {
      const columnIdHierarchy = id.split('.')
      let data = dataItem
      columnIdHierarchy.forEach((columnIdNode: string) => {
        if (typeof data !== 'undefined' && columnIdNode in data) {
          data = data[columnIdNode]
        } else {
          data = undefined
        }
      })
      return data
    },
    getCellText(dataItem: any, column: DataTableColumn): string {
      const data = this.getCellValue(dataItem, column)
      if (Array.isArray(data)) {
        return data.join(', ')
      }
      if (data instanceof Date) {
        return useDateFormat(data)
      }
      if (typeof data === 'number' || typeof data === 'boolean') {
        return data.toLocaleString(this.$epb.locale)
      }
      return data
    },
    getCellTooltip(dataItem: any, column: DataTableColumn): string {
      return (
        (column.tooltipId && this.getCellText(dataItem, { id: column.tooltipId })) ||
        this.getCellText(dataItem, column)
      )
    },
    getChildrenIds(dataItem): string[] {
      if (!dataItem.children) {
        return []
      }
      const ids = []
      dataItem.children.forEach((child: any) => {
        ids.push(child.id)
        this.getChildrenIds(child).forEach((id: string) => {
          ids.push(id)
        })
      })
      return ids
    },
    isDataItemSelected(dataItem: any): boolean {
      return this.isDataItemInArray(dataItem, this.selection)
    },
    isDataItemInArray(dataItem: any, array: any[]): boolean {
      return array.some(
        (currentDataItem: any) => currentDataItem[this.rowsIdField] === dataItem[this.rowsIdField],
      )
    },
  },
})
